import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Sub-branch master (Masters → Sub-branches). A finer-grained office unit a
 * user can be tagged with alongside their Branch. Each sub-branch belongs to a
 * parent Branch (from the Branch master) and carries an optional postal
 * address. Mirrors the Department master shape - see ADR-0016 / ADR-0020.
 */

/** Minimal parent-branch reference embedded on a sub-branch row. */
const BranchRefSchema = z.object({ id: UuidSchema, name: z.string() });

export const SubBranchListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  /** Parent Branch FK + hydrated reference (null only for legacy rows). */
  branchId: UuidSchema.nullable(),
  branch: BranchRefSchema.nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type SubBranchListItem = z.infer<typeof SubBranchListItemSchema>;

export const SubBranchListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type SubBranchListQuery = z.infer<typeof SubBranchListQuerySchema>;

export const SubBranchListResponseSchema = paginatedResponseSchema(SubBranchListItemSchema);
export type SubBranchListResponse = z.infer<typeof SubBranchListResponseSchema>;

export const SubBranchDetailSchema = SubBranchListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type SubBranchDetail = z.infer<typeof SubBranchDetailSchema>;

const SubBranchCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  /** Parent Branch is required - every sub-branch belongs to a branch. */
  branchId: UuidSchema,
  address: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateSubBranchRequestSchema = SubBranchCoreFieldsSchema;
export type CreateSubBranchRequest = z.infer<typeof CreateSubBranchRequestSchema>;

export const UpdateSubBranchRequestSchema = SubBranchCoreFieldsSchema.partial();
export type UpdateSubBranchRequest = z.infer<typeof UpdateSubBranchRequestSchema>;
